/**
 * Geometry helpers for professional connector paths.
 * Orthogonal polylines get filleted corners; mind-map branches use quadratic curves.
 */
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/** Sample a quadratic Bézier into polyline points (flat [x,y,...]). */
export function quadraticToPoints(x0, y0, cx, cy, x1, y1, steps) {
    if (steps === void 0) { steps = 16; }
    var out = [x0, y0];
    var n = Math.max(4, steps);
    for (var i = 1; i <= n; i++) {
        var t = i / n;
        var u = 1 - t;
        out.push(u * u * x0 + 2 * u * t * cx + t * t * x1, u * u * y0 + 2 * u * t * cy + t * t * y1);
    }
    return out;
}
/** SVG path `d` for a quadratic mind-map branch. */
export function quadraticPathD(x0, y0, cx, cy, x1, y1) {
    return "M ".concat(x0, " ").concat(y0, " Q ").concat(cx, " ").concat(cy, " ").concat(x1, " ").concat(y1);
}
/**
 * Replace sharp orthogonal corners with arc fillets approximated as polylines.
 * Keeps endpoints exact; skips fillets when a segment is too short.
 */
export function roundOrthogonalCorners(points, radius, segments) {
    if (radius === void 0) { radius = 10; }
    if (segments === void 0) { segments = 6; }
    if (points.length < 6 || radius <= 0)
        return points.slice();
    var out = [points[0], points[1]];
    for (var i = 2; i < points.length - 2; i += 2) {
        var x0 = points[i - 2];
        var y0 = points[i - 1];
        var x1 = points[i];
        var y1 = points[i + 1];
        var x2 = points[i + 2];
        var y2 = points[i + 3];
        var dx0 = x1 - x0;
        var dy0 = y1 - y0;
        var dx1 = x2 - x1;
        var dy1 = y2 - y1;
        var len0 = Math.hypot(dx0, dy0);
        var len1 = Math.hypot(dx1, dy1);
        // Only fillet true axis-aligned corners
        var orthog = (Math.abs(dx0) < 0.01 || Math.abs(dy0) < 0.01) &&
            (Math.abs(dx1) < 0.01 || Math.abs(dy1) < 0.01) &&
            Math.abs(dx0 * dx1 + dy0 * dy1) < 0.01;
        if (!orthog || len0 < 1 || len1 < 1) {
            out.push(x1, y1);
            continue;
        }
        var r = Math.min(radius, len0 * 0.45, len1 * 0.45);
        if (r < 2.5 || len0 < radius || len1 < radius) {
            out.push(x1, y1);
            continue;
        }
        var ux0 = dx0 / len0;
        var uy0 = dy0 / len0;
        var ux1 = dx1 / len1;
        var uy1 = dy1 / len1;
        var enterX = x1 - ux0 * r;
        var enterY = y1 - uy0 * r;
        var exitX = x1 + ux1 * r;
        var exitY = y1 + uy1 * r;
        out.push(enterX, enterY);
        // Circular arc from enter → exit around corner (x1,y1)
        var startAng = Math.atan2(enterY - y1, enterX - x1);
        var endAng = Math.atan2(exitY - y1, exitX - x1);
        var delta = endAng - startAng;
        while (delta > Math.PI)
            delta -= Math.PI * 2;
        while (delta < -Math.PI)
            delta += Math.PI * 2;
        // Prefer the short turn matching the corner (≤ 90°)
        if (Math.abs(delta) > Math.PI / 2 + 0.01) {
            delta = delta > 0 ? delta - Math.PI * 2 : delta + Math.PI * 2;
        }
        for (var s = 1; s <= segments; s++) {
            var t = s / segments;
            var a = startAng + delta * t;
            out.push(x1 + Math.cos(a) * r, y1 + Math.sin(a) * r);
        }
    }
    out.push(points[points.length - 2], points[points.length - 1]);
    return out;
}
/** Sample a cubic Bézier into polyline points (flat [x,y,...]). */
export function cubicToPoints(x0, y0, x1, y1, x2, y2, x3, y3, steps) {
    if (steps === void 0) { steps = 20; }
    var out = [x0, y0];
    var n = Math.max(6, steps);
    for (var i = 1; i <= n; i++) {
        var t = i / n;
        var u = 1 - t;
        var uu = u * u;
        var tt = t * t;
        var a = uu * u;
        var b = 3 * uu * t;
        var c = 3 * u * tt;
        var d = tt * t;
        out.push(a * x0 + b * x1 + c * x2 + d * x3, a * y0 + b * y1 + c * y2 + d * y3);
    }
    return out;
}
/**
 * Mermaid-style horizontal link: exits source horizontally, enters target horizontally.
 * Smooth cubic when y differs (flowchart LR / mindmap look).
 */
export function mermaidHorizontalLink(x1, y1, x2, y2, steps) {
    if (steps === void 0) { steps = 22; }
    var dx = Math.abs(x2 - x1);
    var pull = Math.max(28, dx * 0.45);
    var dir = x2 >= x1 ? 1 : -1;
    return cubicToPoints(x1, y1, x1 + dir * pull, y1, x2 - dir * pull, y2, x2, y2, steps);
}
/**
 * L-bend as a single cubic — exits along the first leg, enters along the second
 * (string / cable elbow, not a sharp corner).
 */
export function stringLCurve(x0, y0, x1, y1, x2, y2, tension, steps) {
    if (tension === void 0) { tension = 0.58; }
    if (steps === void 0) { steps = 28; }
    var t = Math.min(0.85, Math.max(0.35, tension));
    var c1x = x0 + (x1 - x0) * t;
    var c1y = y0 + (y1 - y0) * t;
    var c2x = x2 + (x1 - x2) * t;
    var c2y = y2 + (y1 - y2) * t;
    return cubicToPoints(x0, y0, c1x, c1y, c2x, c2y, x2, y2, steps);
}
/**
 * U-bend as a single cubic — vertical exit, horizontal span, vertical entry.
 * Control points sit on the rail so the wire bows like a flexible string.
 */
export function stringUCurve(x0, y0, railY, x3, y3, steps) {
    if (steps === void 0) { steps = 32; }
    // Pull controls toward the rail; more separation → deeper, softer U
    var span = Math.abs(railY - y0) + Math.abs(y3 - railY);
    var soft = Math.min(1, Math.max(0.45, span / 120));
    var c1y = y0 + (railY - y0) * (0.55 + soft * 0.25);
    var c2y = y3 + (railY - y3) * (0.55 + soft * 0.25);
    return cubicToPoints(x0, y0, x0, c1y, x3, c2y, x3, y3, steps);
}
/**
 * Horizontal U (C-bend): leave left/right, span vertically, enter left/right.
 */
export function stringCCurve(x0, y0, railX, x3, y3, steps) {
    if (steps === void 0) { steps = 32; }
    var span = Math.abs(railX - x0) + Math.abs(x3 - railX);
    var soft = Math.min(1, Math.max(0.45, span / 120));
    var c1x = x0 + (railX - x0) * (0.55 + soft * 0.25);
    var c2x = x3 + (railX - x3) * (0.55 + soft * 0.25);
    return cubicToPoints(x0, y0, c1x, y0, c2x, y3, x3, y3, steps);
}
function nearlyEqual(a, b, eps) {
    if (eps === void 0) { eps = 1.25; }
    return Math.abs(a - b) < eps;
}
/**
 * Convert an orthogonal polyline skeleton into string-like U / L / S curves.
 * Keeps endpoints exact so arrows stay glued to connection ports.
 */
export function stringCurveFromOrthogonal(points, steps) {
    if (steps === void 0) { steps = 28; }
    if (points.length < 6)
        return points.slice();
    var n = points.length / 2;
    // L: 3 points
    if (n === 3) {
        return stringLCurve(points[0], points[1], points[2], points[3], points[4], points[5], 0.6, steps);
    }
    // U / C / Z: 4 points
    if (n === 4) {
        var x0 = points[0];
        var y0 = points[1];
        var x1 = points[2];
        var y1 = points[3];
        var x2 = points[4];
        var y2 = points[5];
        var x3 = points[6];
        var y3 = points[7];
        // Classic U (TB): vertical, horizontal rail, vertical
        if (nearlyEqual(x0, x1) && nearlyEqual(y1, y2) && nearlyEqual(x2, x3)) {
            return stringUCurve(x0, y0, y1, x3, y3, steps);
        }
        // Classic C (LR): horizontal, vertical rail, horizontal
        if (nearlyEqual(y0, y1) && nearlyEqual(x1, x2) && nearlyEqual(y2, y3)) {
            return stringCCurve(x0, y0, x1, x3, y3, steps);
        }
        // Z / S: chain two L curves
        var a = stringLCurve(x0, y0, x1, y1, x2, y2, 0.55, Math.max(12, Math.floor(steps / 2)));
        var b = stringLCurve(x1, y1, x2, y2, x3, y3, 0.55, Math.max(12, Math.floor(steps / 2)));
        // Join without duplicating the middle vertex
        return a.concat(b.slice(2));
    }
    // Longer paths: adaptive large fillets (cable around corners)
    var minSeg = Infinity;
    for (var i = 0; i < points.length - 2; i += 2) {
        minSeg = Math.min(minSeg, Math.hypot(points[i + 2] - points[i], points[i + 3] - points[i + 1]));
    }
    var radius = Math.min(36, Math.max(14, minSeg * 0.48));
    return roundOrthogonalCorners(points, radius, 14);
}
/**
 * Smooth path through free waypoints (string through beads).
 * Uses Catmull-Rom → cubic segments so bends feel continuous while dragging.
 */
export function pathThroughWaypoints(x1, y1, waypoints, x2, y2, stepsPerSeg) {
    var e_1, _a;
    if (stepsPerSeg === void 0) { stepsPerSeg = 14; }
    var pts = [x1, y1];
    try {
        for (var waypoints_1 = __values(waypoints), waypoints_1_1 = waypoints_1.next(); !waypoints_1_1.done; waypoints_1_1 = waypoints_1.next()) {
            var w = waypoints_1_1.value;
            pts.push(w.x, w.y);
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (waypoints_1_1 && !waypoints_1_1.done && (_a = waypoints_1.return)) _a.call(waypoints_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    pts.push(x2, y2);
    if (pts.length <= 4)
        return pts.slice();
    return catmullRomToPoints(pts, stepsPerSeg);
}
/** Uniform Catmull-Rom spline sampled as a flat polyline. */
export function catmullRomToPoints(points, stepsPerSeg) {
    if (stepsPerSeg === void 0) { stepsPerSeg = 12; }
    var n = points.length / 2;
    if (n < 2)
        return points.slice();
    if (n === 2)
        return points.slice();
    var out = [points[0], points[1]];
    var get = function (i) {
        var j = Math.max(0, Math.min(n - 1, i));
        return { x: points[j * 2], y: points[j * 2 + 1] };
    };
    for (var i = 0; i < n - 1; i++) {
        var p0 = get(i - 1);
        var p1 = get(i);
        var p2 = get(i + 1);
        var p3 = get(i + 2);
        // Convert Catmull-Rom to cubic Bezier controls
        var c1x = p1.x + (p2.x - p0.x) / 6;
        var c1y = p1.y + (p2.y - p0.y) / 6;
        var c2x = p2.x - (p3.x - p1.x) / 6;
        var c2y = p2.y - (p3.y - p1.y) / 6;
        var seg = cubicToPoints(p1.x, p1.y, c1x, c1y, c2x, c2y, p2.x, p2.y, stepsPerSeg);
        out.push.apply(out, __spreadArray([], __read(seg.slice(2)), false));
    }
    return out;
}
/**
 * Nearest point on a polyline to (x,y), plus insertion index for a new vertex
 * after the segment that contains the projection (1-based vertex index).
 */
export function nearestPointOnPolyline(points, x, y) {
    var best = { x: points[0], y: points[1], segIndex: 0, dist: Infinity, t: 0 };
    for (var i = 0; i < points.length - 2; i += 2) {
        var x0 = points[i];
        var y0 = points[i + 1];
        var x1 = points[i + 2];
        var y1 = points[i + 3];
        var dx = x1 - x0;
        var dy = y1 - y0;
        var lenSq = dx * dx + dy * dy;
        var t = lenSq < 1e-8 ? 0 : Math.max(0, Math.min(1, ((x - x0) * dx + (y - y0) * dy) / lenSq));
        var px = x0 + dx * t;
        var py = y0 + dy * t;
        var dist = Math.hypot(x - px, y - py);
        if (dist < best.dist) {
            best = { x: px, y: py, segIndex: i / 2, dist: dist, t: t };
        }
    }
    return best;
}
/**
 * Mermaid flowchart TB bus geometry for parent → children.
 * Multi-child: shared stem + rail + drops; each drop path is also available
 * as a rounded elbow for professional T-junctions.
 */
export function mermaidOrgBusPaths(parentX, parentBottomY, children, _cornerRadius) {
    if (_cornerRadius === void 0) { _cornerRadius = 10; }
    if (children.length === 0) {
        return { stem: [], bus: [], drops: [], elbows: [] };
    }
    if (children.length === 1) {
        var c = children[0];
        var midY = (parentBottomY + c.topY) / 2;
        // Single child: soft U / L string curve
        var path = stringCurveFromOrthogonal([parentX, parentBottomY, parentX, midY, c.x, midY, c.x, c.topY], 28);
        return { stem: path, bus: [], drops: [], elbows: [path] };
    }
    var tops = children.map(function (c) { return c.topY; });
    var minTop = Math.min.apply(Math, __spreadArray([], __read(tops), false));
    // Bus sits ~42% down the gap so stem and drops have room for fillets.
    var gap = Math.max(28, minTop - parentBottomY);
    var busY = parentBottomY + Math.max(24, gap * 0.42);
    var xs = children.map(function (c) { return c.x; });
    var minX = Math.min.apply(Math, __spreadArray(__spreadArray([], __read(xs), false), [parentX], false));
    var maxX = Math.max.apply(Math, __spreadArray(__spreadArray([], __read(xs), false), [parentX], false));
    var elbows = children.map(function (c) {
        return stringCurveFromOrthogonal([parentX, parentBottomY, parentX, busY, c.x, busY, c.x, c.topY], 26);
    });
    return {
        stem: [parentX, parentBottomY, parentX, busY],
        bus: [minX, busY, maxX, busY],
        drops: children.map(function (c) { return [c.x, busY, c.x, c.topY]; }),
        elbows: elbows,
    };
}
